/**
 * 관리자 API 클라이언트.
 * - base: /api/v1/admin (관리자 전용 라우트)
 * - adminToken 을 Authorization: Bearer 로 자동 첨부
 * - 401 응답 시 세션 폐기 후 에러 throw
 * 공유 계약(@kome/shared)의 admin DTO 를 사용한다.
 */
import { API_BASE } from '@kome/shared';
import type {
  AdminAuditResponse,
  AdminCreateUserBody,
  AdminLoginBody,
  AdminLoginResponse,
  AdminResetPasswordBody,
  AdminStatsResponse,
  AdminTableResponse,
  AdminUpdateUserBody,
  AdminUsersResponse,
  ApiError,
  User,
} from '@kome/shared';
import { clearAdminSession, getAdminToken } from './store';

/**
 * 관리자 API base: <API 루트>/admin
 * - 빌드 시 VITE_API_BASE(절대 Worker URL)가 주입되면 그걸 사용(다른 오리진 호출),
 *   없으면 동일 오리진 /api/v1 (개발 시 Vite 프록시).
 * 주의: 관리자 웹과 Worker 는 오리진이 다르므로 운영 빌드에는 VITE_API_BASE 가 반드시 필요하다.
 */
const API_ROOT = (import.meta.env.VITE_API_BASE as string | undefined) || API_BASE;
const ADMIN_BASE = `${API_ROOT}/admin`;

/** API 호출 실패를 표현하는 에러 (status + 표준 ApiError 포함) */
export class AdminApiError extends Error {
  status: number;
  code?: string;
  detail?: unknown;
  constructor(status: number, body: ApiError) {
    super(body.error || `요청 실패 (HTTP ${status})`);
    this.name = 'AdminApiError';
    this.status = status;
    this.code = body.code;
    this.detail = body.detail;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  /** 인증 토큰 첨부 안 함 (로그인 호출용) */
  noAuth?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${ADMIN_BASE}${path}`;
  if (!query) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${url}?${s}` : url;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (!opts.noAuth) {
    const token = getAdminToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch (e) {
    throw new AdminApiError(0, { error: '서버에 연결할 수 없습니다.', detail: String(e) });
  }

  // 미인증/만료 → 세션 폐기 (가드가 /login 으로 보냄)
  if (res.status === 401) {
    clearAdminSession();
  }

  let payload: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  // 서버 표준 봉투: { ok: true, data } | { ok: false, error }
  const envelope =
    payload && typeof payload === 'object' && 'ok' in (payload as object)
      ? (payload as { ok: boolean; data?: T; error?: ApiError })
      : null;

  if (!res.ok || (envelope && envelope.ok === false)) {
    const body: ApiError =
      envelope?.error ??
      (payload && typeof payload === 'object' && 'error' in (payload as object)
        ? (payload as ApiError)
        : { error: typeof payload === 'string' && payload ? payload : `요청 실패 (HTTP ${res.status})` });
    throw new AdminApiError(res.status, body);
  }

  // 봉투를 쓰면 data 만, 아니면(평문) payload 그대로 반환
  return (envelope ? (envelope.data as T) : (payload as T));
}

// ───────────────────────── 인증
/** 관리자 코드로 로그인 → adminToken 발급 */
export function login(code: string): Promise<AdminLoginResponse> {
  const body: AdminLoginBody = { code };
  return request<AdminLoginResponse>('/login', { method: 'POST', body, noAuth: true });
}

// ───────────────────────── 사용자
export const users = {
  /** 사용자 목록 (검색/페이지) */
  list(params?: { q?: string; limit?: number; offset?: number }): Promise<AdminUsersResponse> {
    return request<AdminUsersResponse>('/users', { query: params });
  },
  /** 계정 생성 (서버는 생성된 User 를 직접 반환) */
  create(body: AdminCreateUserBody): Promise<User> {
    return request<User>('/users', { method: 'POST', body });
  },
  /** 계정 수정 (서버는 갱신된 User 를 직접 반환) */
  update(id: string, body: AdminUpdateUserBody): Promise<User> {
    return request<User>(`/users/${encodeURIComponent(id)}`, { method: 'PATCH', body });
  },
  /** 비밀번호 재설정 */
  resetPassword(id: string, password: string): Promise<{ ok: true }> {
    const body: AdminResetPasswordBody = { password };
    return request<{ ok: true }>(`/users/${encodeURIComponent(id)}/reset-password`, {
      method: 'POST',
      body,
    });
  },
};

// ───────────────────────── 모니터링
/** 실시간 통계 (접속자·메시지량·방·AI 사용량) */
export function stats(): Promise<AdminStatsResponse> {
  return request<AdminStatsResponse>('/stats');
}

// ───────────────────────── DB 조회
/** 임의 테이블 조회 (페이지네이션) */
export function dbTable(table: string, limit = 50, offset = 0): Promise<AdminTableResponse> {
  return request<AdminTableResponse>(`/db/${encodeURIComponent(table)}`, { query: { limit, offset } });
}

// ───────────────────────── 감사 로그
/** admin_audit 로그 조회 */
export function audit(params?: { limit?: number; offset?: number }): Promise<AdminAuditResponse> {
  return request<AdminAuditResponse>('/audit', { query: params });
}

export const adminApi = { login, users, stats, dbTable, audit };
export default adminApi;
